/**
 * Tenant theming — curated accent presets applied as CSS custom properties.
 *
 * Only the --theme-accent-* group is themable (see theme.css); neutrals,
 * danger, success, and shape tokens stay fixed so a tenant's choice can never
 * break contrast or layout. Presets are curated (not arbitrary hex) so every
 * derived shade — tint band, active border, ink-on-tint, focus ring — is
 * hand-checked for contrast.
 *
 * The catalog is spaced by HUE so that no two presets read as the same choice
 * (27° 142° 175° 201° 215°(desaturated) 244° 295° 344°). Two pairs used to
 * collide — teal/emerald sat 15° apart and indigo/violet 18°, close enough
 * that neither could be told from the other in a swatch grid. Emerald moved
 * to a true green and violet to magenta, and Ocean was added because a plain
 * blue is the first colour most dealerships reach for and the list had none.
 *
 * Preset KEYS are stored in the database, so they never change; only labels
 * and hexes do. The server keeps a matching allow-list in
 * node/routes/themeSettings.js — add there first, or the save 400s.
 *
 * The dealership's choice is stored in Dealerships.ThemeSettings (JSON,
 * `{ "preset": "teal" }`) and arrives with the session payload; Cars.jsx
 * calls applyTheme() once on login and again when Settings saves a change.
 */

export const DEFAULT_THEME = "indigo";

export const THEME_PRESETS = {
  indigo: {
    label: "Indigo",
    accent: "#4f46e5",
    hover: "#4338ca",
    tint: "#eef0fd",
    border: "#c7cdf5",
    ink: "#3730a3",
    ring: "rgba(79, 70, 229, 0.28)",
  },
  ocean: {
    label: "Ocean",
    accent: "#0369a1",
    hover: "#075985",
    tint: "#e6f1f8",
    border: "#b8d6e9",
    ink: "#075985",
    ring: "rgba(3, 105, 161, 0.28)",
  },
  teal: {
    label: "Teal",
    accent: "#0f766e",
    hover: "#0c5f59",
    tint: "#e7f1ef",
    border: "#c2ddd7",
    ink: "#115e59",
    ring: "rgba(15, 118, 110, 0.28)",
  },
  //Key stays "emerald" (it is in the database); the colour moved off teal's
  //shoulder to a true green.
  emerald: {
    label: "Forest",
    accent: "#15803d",
    hover: "#166534",
    tint: "#e9f5ec",
    border: "#bfe0c9",
    ink: "#14532d",
    ring: "rgba(21, 128, 61, 0.28)",
  },
  crimson: {
    label: "Crimson",
    accent: "#be123c",
    hover: "#9f1239",
    tint: "#fdecf0",
    border: "#f5c2cf",
    ink: "#9f1239",
    ring: "rgba(190, 18, 60, 0.28)",
  },
  amber: {
    label: "Amber",
    accent: "#b45309",
    hover: "#92400e",
    tint: "#fdf3e7",
    border: "#f2d9b5",
    ink: "#92400e",
    ring: "rgba(180, 83, 9, 0.28)",
  },
  //Key stays "violet" (it is in the database); the colour moved off indigo's
  //shoulder into magenta.
  violet: {
    label: "Plum",
    accent: "#a21caf",
    hover: "#86198f",
    tint: "#fbecfd",
    border: "#eec9f2",
    ink: "#86198f",
    ring: "rgba(162, 28, 175, 0.28)",
  },
  slate: {
    label: "Slate",
    accent: "#475569",
    hover: "#334155",
    tint: "#eef2f6",
    border: "#cbd5e1",
    ink: "#334155",
    ring: "rgba(71, 85, 105, 0.28)",
  },
};

/** #rrggbb -> { h: 0..360, s: 0..1, l: 0..1 }. Exported for the unit test. */
export function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  //An unparseable accent falls back to the slate the hero used to be, so a bad
  //value degrades to the old appearance rather than to black.
  if (!m) return { h: 215, s: 0.2, l: 0.35 };
  const int = parseInt(m[1], 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const l = (max + min) / 2;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === r) h = 60 * (((g - b) / d) % 6);
  else if (max === g) h = 60 * ((b - r) / d + 2);
  else h = 60 * ((r - g) / d + 4);
  return { h: (h + 360) % 360, s, l };
}

/**
 * The record modal's hero gradient, in the tenant's colour.
 *
 * The hero used to be a fixed slate, and the reason given in carstyles.css was
 * that it "has to sit under every theme preset without clashing". The owner's
 * call on 2026-09-13 is that it should follow the theme instead. That reason was
 * only ever half right: nothing accent-coloured sits on the hero — the stepper,
 * the finder and the close button all take a white glass treatment, and the
 * status pill is overridden to glass inside .rec-header — so there is nothing up
 * there for an accent-tinted band to fight.
 *
 * WHAT IS PRESERVED IS LIGHTNESS, exactly. These are the five stops of the
 * original slate gradient with their L values untouched and only H and S moved,
 * because lightness is the whole of the contrast budget for 32px white type over
 * this band: rotating a hue cannot make the title less legible, and picking five
 * fresh colours per preset could. So the hero is the same gradient it always
 * was, wearing the tenant's hue.
 *
 * SATURATION SCALES WITH THE ACCENT'S OWN, which is what stops this reading as
 * either a grey smudge or a paint chart. The "Slate" preset's accent is only 19%
 * saturated, so it lands back within a point or two of the original and that
 * preset looks untouched; Indigo and Ocean are 75-96% saturated and get a hero
 * that is unmistakably indigo or blue. Capped so no preset can drive a stop to
 * a fully saturated colour, which at these lightnesses would read as a warning.
 *
 * Pure, and exported for the unit test — the eight presets' output is checked
 * there rather than eyeballed from eight screenshots.
 */
const HERO_STOPS = {
  //[saturation, lightness] of #6b7a90, #94a3b8, #334155, #2b3547, #1e293b.
  glow: [0.147, 0.492],   //top-left radial, the light that gives it depth
  lift: [0.202, 0.651],   //lightest stop of the base gradient
  mid: [0.250, 0.267],    //its 62% stop
  pool: [0.246, 0.224],   //bottom-right radial
  deep: [0.326, 0.175],   //the dark corner the title is read against
};

export function heroStops(accentHex) {
  const { h, s } = hexToHsl(accentHex);
  //0.19 (Slate) lands on ~0.94, i.e. the original. 0.75+ roughly doubles.
  const k = Math.min(2.2, 0.6 + 1.8 * s);
  const out = {};
  for (const [name, [baseS, l]] of Object.entries(HERO_STOPS)) {
    const sat = Math.min(0.68, baseS * k);
    out[name] = `hsl(${Math.round(h)}, ${Math.round(sat * 100)}%, ${Math.round(l * 1000) / 10}%)`;
  }
  return out;
}

/** Apply a preset's accent group to <html>. Unknown names fall back to the default. */
export function applyTheme(presetName) {
  const preset = THEME_PRESETS[presetName] || THEME_PRESETS[DEFAULT_THEME];
  const s = document.documentElement.style;
  const hero = heroStops(preset.accent);
  for (const [name, value] of Object.entries(hero)) {
    s.setProperty(`--theme-hero-${name}`, value);
  }
  s.setProperty("--theme-accent", preset.accent);
  s.setProperty("--theme-accent-hover", preset.hover);
  s.setProperty("--theme-accent-fg", "#ffffff");
  s.setProperty("--theme-accent-tint", preset.tint);
  s.setProperty("--theme-accent-border", preset.border);
  s.setProperty("--theme-accent-ink", preset.ink);
  s.setProperty("--theme-ring", preset.ring);
  //Browser chrome tint (Android address bar, PWA title bar) follows the
  //tenant accent instead of the static index.html value (plan 5.6).
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) themeColorMeta.setAttribute("content", preset.accent);
}

/**
 * Resolved accent hex for JS consumers that can't take a CSS variable
 * (react-joyride's primaryColor, chart libs). Read at render time so it
 * follows the applied tenant theme.
 */
export function themeAccent() {
  return (
    getComputedStyle(document.documentElement).getPropertyValue("--theme-accent").trim() ||
    THEME_PRESETS[DEFAULT_THEME].accent
  );
}

/** Parse Dealerships.ThemeSettings (JSON string or object) to a preset name. */
export function presetFromThemeSettings(themeSettings) {
  if (!themeSettings) return DEFAULT_THEME;
  try {
    const obj = typeof themeSettings === "string" ? JSON.parse(themeSettings) : themeSettings;
    return obj && THEME_PRESETS[obj.preset] ? obj.preset : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}
